import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Trophy, Heart, Star } from 'lucide-react';
import { publicAPI } from '../lib/api';

export default function HallOfHonourPage() {
  const [donors, setDonors] = useState([]);
  const [generalDonors, setGeneralDonors] = useState([]);
  const [corporateDonors, setCorporateDonors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, count: 0 });

  useEffect(() => {
    fetch('/api/public/donors')
      .then(r => r.json())
      .then(data => {
        setDonors(data.donors || []);
        setGeneralDonors(data.general_donors || []);
        setCorporateDonors(data.corporate_donors || []);
        setStats(data.stats || { total: 0, count: 0 });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const formatCurrency = (n) => `₦${Number(n).toLocaleString('en-NG')}`;

  const getRankStyle = (i) => {
    if (i === 0) return { color: '#c9a227', fontSize: '1.3rem' };
    if (i === 1) return { color: '#c0c0c0', fontSize: '1.1rem' };
    if (i === 2) return { color: '#cd7f32', fontSize: '1rem' };
    return { color: 'var(--text-muted)', fontSize: '0.9rem' };
  };

  const namedDonors = [...donors.filter(d => !d.anonymous), ...generalDonors].sort((a, b) => b.total - a.total);
  const anonCount = donors.filter(d => d.anonymous).length;

  return (
    <div style={{ background: 'var(--surface-body)', minHeight: '100vh', color: 'var(--text-primary)' }}>
      {/* Nav */}
      <nav style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 40px', background: 'rgba(10,12,20,0.85)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          <ArrowLeft size={16} /> Back to Home
        </Link>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem', color: 'var(--gold)' }}>Futurum Innovation Awards</div>
        <Link to="/register" className="btn btn-primary btn-sm">Join Season II</Link>
      </nav>

      {/* Hero */}
      <div style={{ paddingTop: 72, background: 'linear-gradient(135deg, rgba(201,162,39,0.08) 0%, rgba(45,212,191,0.03) 100%)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '80px 40px 60px', textAlign: 'center' }}>
          <div className="eyebrow" style={{ marginBottom: 16 }}>Alumni Giving · All Seasons</div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2.5rem, 6vw, 4rem)', fontWeight: 300, lineHeight: 1.1, marginBottom: 20 }}>
            Hall of <span style={{ color: 'var(--gold)', fontStyle: 'italic' }}>Honour</span>
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '1.05rem', maxWidth: 560, margin: '0 auto 40px', lineHeight: 1.7 }}>
            These alumni are shaping the next generation of Nigerian engineers. Every contribution — large or small — funds real research, real projects, and real futures.
          </p>

          {/* Stats */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 48, flexWrap: 'wrap' }}>
            {[
              [formatCurrency(stats.total || 0), 'Total Pledged'],
              [stats.count || donors.length, 'Donors'],
              [stats.seasons || 1, 'Seasons'],
            ].map(([val, label]) => (
              <div key={label} style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--gold)', fontWeight: 600 }}>{val}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 4, fontFamily: 'var(--font-mono)' }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '60px 40px' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)' }}>Loading…</div>
        ) : donors.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Heart size={40} style={{ color: 'var(--gold)', margin: '0 auto 16px', opacity: 0.5 }} />
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', fontWeight: 400, marginBottom: 8 }}>Be the first donor</h2>
            <p style={{ color: 'var(--text-muted)', marginBottom: 24 }}>Your name could be at the top of this board.</p>
            <Link to="/login" className="btn btn-primary">Sponsor a Project</Link>
          </div>
        ) : (
          <>
            {/* Top 3 spotlight */}
            {namedDonors.length > 0 && (
              <div style={{ marginBottom: 48 }}>
                <div className="eyebrow" style={{ marginBottom: 24, textAlign: 'center' }}>Top Contributors</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                  {namedDonors.slice(0, 3).map((donor, i) => (
                    <div key={donor.id} className="card" style={{
                      textAlign: 'center', padding: 28,
                      border: i === 0 ? '1px solid rgba(201,162,39,0.4)' : i === 1 ? '1px solid rgba(192,192,192,0.3)' : '1px solid rgba(205,127,50,0.3)',
                      background: i === 0 ? 'linear-gradient(135deg, rgba(201,162,39,0.08) 0%, transparent 100%)' : 'var(--surface-card)',
                      position: 'relative', overflow: 'hidden'
                    }}>
                      {i === 0 && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, var(--gold), transparent)' }} />}
                      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 4, ...getRankStyle(i) }}>#{i + 1}</div>
                      {i === 0 && <Trophy size={24} style={{ color: 'var(--gold)', margin: '0 auto 8px' }} />}
                      <div style={{ fontWeight: 600, fontSize: '1rem', marginBottom: 4 }}>{donor.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 12 }}>{donor.company || 'Alumni'}</div>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--gold)', fontWeight: 600 }}>{formatCurrency(donor.total)}</div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>{donor.sponsorship_count > 0 ? donor.sponsorship_count + ' sponsorship' + (donor.sponsorship_count !== 1 ? 's' : '') : 'General Donor'}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Full leaderboard */}
            <div style={{ marginBottom: 40 }}>
              <div className="eyebrow" style={{ marginBottom: 20 }}>Full Leaderboard</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {namedDonors.map((donor, i) => (
                  <div key={donor.id} style={{
                    display: 'flex', alignItems: 'center', gap: 16,
                    padding: '14px 20px', borderRadius: 10,
                    background: i < 3 ? 'rgba(201,162,39,0.04)' : 'var(--surface-card)',
                    border: '1px solid var(--border-card)',
                    transition: 'background 0.2s'
                  }}>
                    <div style={{ width: 32, textAlign: 'center', fontFamily: 'var(--font-mono)', fontWeight: 700, flexShrink: 0, ...getRankStyle(i) }}>
                      {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: '0.95rem' }}>{donor.name}</div>
                      {donor.company && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{donor.company}</div>}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontFamily: 'var(--font-display)', color: 'var(--gold)', fontWeight: 600, fontSize: '1.05rem' }}>{formatCurrency(donor.total)}</div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{donor.sponsorship_count > 0 ? donor.sponsorship_count + ' sponsorship' + (donor.sponsorship_count !== 1 ? 's' : '') : 'General Donor'}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Corporate Sponsors */}
            {corporateDonors.length > 0 && (
              <div style={{ marginBottom: 40 }}>
                <div className="eyebrow" style={{ marginBottom: 20 }}>Corporate Sponsors</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {corporateDonors.map((donor, i) => (
                    <div key={donor.id} style={{
                      display: 'flex', alignItems: 'center', gap: 16,
                      padding: '16px 20px', borderRadius: 10,
                      background: 'rgba(201,162,39,0.04)',
                      border: '1px solid rgba(201,162,39,0.15)',
                    }}>
                      <div style={{ width: 40, height: 40, borderRadius: 8, background: 'rgba(201,162,39,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ fontSize: '1.2rem' }}>🏢</span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, fontSize: '1rem', color: 'var(--text-primary)' }}>{donor.name}</div>
                        {donor.tier && (
                          <div style={{ fontSize: '0.7rem', color: 'var(--gold)', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', marginTop: 2 }}>
                            {donor.tier.toUpperCase()} SPONSOR
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontFamily: 'var(--font-display)', color: 'var(--gold)', fontWeight: 600, fontSize: '1.1rem' }}>{formatCurrency(donor.total)}</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>Corporate Sponsor</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Community Builders */}
            <div style={{ padding: 28, borderRadius: 16, border: '1px solid rgba(45,212,191,0.15)', background: 'rgba(45,212,191,0.03)', textAlign: 'center' }}>
              <Heart size={24} style={{ color: 'var(--teal)', margin: '0 auto 12px' }} />
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 400, marginBottom: 8 }}>Community Builders</h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', lineHeight: 1.7, marginBottom: 16 }}>
                {anonCount > 0
                  ? `${anonCount} donor${anonCount !== 1 ? 's' : ''} chose to give anonymously. Every contribution — named or not — directly funds student research at OAU.`
                  : 'Every contribution directly funds student research at OAU. No gift is too small to make a difference.'
                }
              </p>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                "The best investment you can make is in the education of the next generation."
              </p>
            </div>

            {/* CTA */}
            <div style={{ textAlign: 'center', marginTop: 48 }}>
              <div className="eyebrow" style={{ marginBottom: 12 }}>Your name could be here</div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.75rem', fontWeight: 400, marginBottom: 16 }}>
                Ready to <span style={{ color: 'var(--gold)', fontStyle: 'italic' }}>climb the board?</span>
              </h3>
              <p style={{ color: 'var(--text-secondary)', marginBottom: 24, fontSize: '0.9rem' }}>
                {namedDonors.length > 0 && `The current #1 donor has given ${formatCurrency(namedDonors[0]?.total)}. `}
                Sponsor a research topic today and join this community of changemakers.
              </p>
              <Link to="/login" className="btn btn-primary btn-lg">Sponsor a Project</Link>
            </div>
          </>
        )}
      </div>

      <footer style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '24px 40px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
        Futurum Innovation Awards · Pervium Consulting × OAU EEE
      </footer>
    </div>
  );
}
